// PURE particle system of the board FX (IMPLEMENTATION §2.9 "particles 150 alive (Low: 75)", UX §5 caps:
// "≥ 80%: halve new spawns. ≥ 100%: skip cosmetic spawns"). Particles are analytic: position, size, alpha
// and angle are closed-form functions of the age on the FX clock (launch velocity, exponential drag,
// gravity, one optional floor bounce), so a frozen clock freezes them exactly and the frame rate never
// changes a trajectory. Randomness comes from a seeded mulberry32 (same seed + same bursts = same particles).
import { mulberry32, type Rand } from '../../art/rng.ts';

/** A juice.json `particles` preset (cells, seconds, degrees). */
export interface ParticlePreset {
  frame: string;
  lifeMs: number;
  speedMin: number;
  speedMax: number;
  dirDeg: number;
  spreadDeg: number;
  gravity: number;
  drag: number;
  sizeFrom: number;
  sizeTo: number;
  alphaFrom: number;
  alphaTo: number;
  spinDeg: number;
  bounce: number;
  floor: number;
  additive: boolean;
  align: boolean;
}

export interface Particle {
  /** Atlas frame id. */
  frame: string;
  /** Multiply tint 0xrrggbb (−1 = none). */
  tint: number;
  add: boolean;
  depth: number;
  // launch state (world units, ms, degrees)
  born: number;
  life: number;
  x0: number;
  y0: number;
  vx: number;
  vy: number;
  g: number;
  k: number;
  s0: number;
  s1: number;
  a0: number;
  a1: number;
  rot0: number;
  spin: number;
  align: boolean;
  floorY: number | null;
  bounce: number;
  // evaluated at the last step
  x: number;
  y: number;
  s: number;
  a: number;
  rot: number;
}

export interface Emit {
  x: number;
  y: number;
  /** World units per cell (preset speeds / sizes are in cells). */
  wu: number;
  depth: number;
  tint?: number;
  /** Rotates the preset's direction (e.g. along a gun barrel). */
  turnDeg?: number;
  /** Frame ids to pick from (confetti colours); default `fx.<preset.frame>`. */
  frames?: readonly string[];
  /** Extra launch delay in ms (a staggered burst). */
  delayMs?: number;
  /**
   * World-unit bounds the launch may not carry a particle past (sides and top): a particle whose drift
   * would leave them is launched slower (same direction, same random draws). Gravity (≥ 0) only pulls it
   * down afterwards. The Gallery confetti keep within juice.json galleryFire.confettiOverflowCells.
   */
  bounds?: { left: number; right: number; top: number };
}

export interface ParticleOptions {
  cap: number;
  throttleAt: number;
  seed: number;
  /** Frame id of a preset texture name (`fx.spark`, `fx.puff` for smoke). */
  frameOf(name: string): string;
}

const DEG = Math.PI / 180;

export class Particles {
  private live: Particle[] = [];
  private capN: number;
  private readonly throttleAt: number;
  private rng: Rand;
  private readonly seed: number;
  private readonly frameOf: (name: string) => string;
  private peakN = 0;
  private skippedN = 0;
  private emittedN = 0;

  constructor(o: ParticleOptions) {
    this.capN = o.cap;
    this.throttleAt = o.throttleAt;
    this.seed = o.seed;
    this.rng = mulberry32(o.seed);
    this.frameOf = o.frameOf;
  }

  /** Low quality lowers the cap (juice caps.particlesLow). */
  setCap(n: number): void {
    this.capN = n;
  }

  get cap(): number {
    return this.capN;
  }

  /**
   * How many of `n` requested particles may spawn now: none at the cap, half above `throttleAt` of it, and
   * never past it.
   */
  allowance(n: number): number {
    const alive = this.live.length;
    if (n <= 0 || alive >= this.capN) return 0;
    let k = n;
    if (alive >= this.throttleAt * this.capN) k = Math.ceil(k / 2);
    return Math.min(k, this.capN - alive);
  }

  /** Launches up to `n` particles of `p` at clock time `now`; returns how many spawned. */
  emit(p: ParticlePreset, n: number, at: Emit, now: number): number {
    const k = this.allowance(n);
    this.skippedN += Math.max(0, n - k);
    const r = this.rng;
    for (let i = 0; i < k; i++) {
      const dir = (p.dirDeg + (at.turnDeg ?? 0) + (r() - 0.5) * p.spreadDeg) * DEG;
      const speed = (p.speedMin + (p.speedMax - p.speedMin) * r()) * at.wu;
      const frames = at.frames;
      const frame =
        frames && frames.length > 0
          ? (frames[Math.floor(r() * frames.length) % frames.length] as string)
          : this.frameOf(p.frame);
      let vx = Math.cos(dir) * speed;
      let vy = Math.sin(dir) * speed;
      if (at.bounds !== undefined) {
        const slow = fitLaunch(vx, vy, at.x, at.y, launchReach(p), at.bounds);
        vx *= slow;
        vy *= slow;
      }
      const part: Particle = {
        frame,
        tint: at.tint ?? -1,
        add: p.additive,
        depth: at.depth,
        born: now + (at.delayMs ?? 0),
        life: p.lifeMs,
        x0: at.x,
        y0: at.y,
        vx,
        vy,
        g: p.gravity * at.wu,
        k: p.drag,
        s0: p.sizeFrom * at.wu,
        s1: p.sizeTo * at.wu,
        a0: p.alphaFrom,
        a1: p.alphaTo,
        rot0: r() * 360,
        spin: p.spinDeg * (r() * 2 - 1),
        align: p.align,
        floorY: p.bounce > 0 && p.drag === 0 && p.gravity > 0 ? at.y + p.floor * at.wu : null,
        bounce: p.bounce,
        x: at.x,
        y: at.y,
        s: p.sizeFrom * at.wu,
        a: at.delayMs !== undefined && at.delayMs > 0 ? 0 : p.alphaFrom,
        rot: 0,
      };
      evaluate(part, now);
      this.live.push(part);
    }
    this.emittedN += k;
    this.peakN = Math.max(this.peakN, this.live.length);
    return k;
  }

  /** Evaluates every particle at clock time `now` and drops the dead. */
  step(now: number): void {
    let dead = false;
    for (const p of this.live) {
      if (now - p.born >= p.life) {
        p.a = -1;
        dead = true;
      } else evaluate(p, now);
    }
    if (dead) this.live = this.live.filter((p) => p.a >= 0);
  }

  list(): readonly Particle[] {
    return this.live;
  }

  clear(): void {
    this.live = [];
  }

  /** Back to the seed (a rebuild: the same bursts replay the same particles). */
  reseed(): void {
    this.rng = mulberry32(this.seed);
  }

  get alive(): number {
    return this.live.length;
  }

  get peak(): number {
    return this.peakN;
  }

  /** Particles refused by the cap / throttle. */
  get skipped(): number {
    return this.skippedN;
  }

  get emitted(): number {
    return this.emittedN;
  }

  resetPeak(): void {
    this.peakN = this.live.length;
  }
}

/** Closed-form state of `p` at clock time `now` (before its launch: hidden at the emitter). */
export function evaluate(p: Particle, now: number): void {
  const ms = now - p.born;
  if (ms < 0) {
    p.x = p.x0;
    p.y = p.y0;
    p.a = 0;
    p.s = p.s0;
    p.rot = p.rot0;
    return;
  }
  const t = ms / 1000;
  const u = Math.min(1, ms / p.life);
  let x: number;
  let y: number;
  let vx: number;
  let vy: number;
  if (p.k > 0) {
    const e = Math.exp(-p.k * t);
    const f = (1 - e) / p.k;
    x = p.x0 + p.vx * f;
    y = p.y0 + p.vy * f + (p.g / p.k) * (t - f);
    vx = p.vx * e;
    vy = p.vy * e + (p.g / p.k) * (1 - e);
  } else {
    x = p.x0 + p.vx * t;
    y = p.y0 + p.vy * t + 0.5 * p.g * t * t;
    vx = p.vx;
    vy = p.vy + p.g * t;
    if (p.floorY !== null && p.g > 0) {
      const t1 = landTime(p.y0, p.vy, p.g, p.floorY);
      if (t1 !== null && t > t1) {
        // one bounce: restitution on both axes, then it rests on the floor
        const vy1 = -(p.vy + p.g * t1) * p.bounce;
        const vx1 = p.vx * p.bounce;
        const x1 = p.x0 + p.vx * t1;
        const t2 = t1 + (2 * -vy1) / p.g;
        const tb = Math.min(t, t2) - t1;
        x = x1 + vx1 * tb;
        y = t >= t2 ? p.floorY : p.floorY + vy1 * tb + 0.5 * p.g * tb * tb;
        vx = t >= t2 ? 0 : vx1;
        vy = t >= t2 ? 0 : vy1 + p.g * tb;
      }
    }
  }
  p.x = x;
  p.y = y;
  p.s = p.s0 + (p.s1 - p.s0) * u;
  p.a = p.a0 + (p.a1 - p.a0) * u;
  p.rot = p.align ? Math.atan2(vy, vx) / DEG + 90 : p.rot0 + p.spin * t;
}

/**
 * How far a launch velocity of 1 (world unit per second) carries a particle of `p` over its life, ignoring
 * gravity: T with no drag, (1 − e^(−kT)) / k with drag k (the drift is monotonic, so this is its maximum).
 */
export function launchReach(p: Pick<ParticlePreset, 'lifeMs' | 'drag'>): number {
  const t = p.lifeMs / 1000;
  return p.drag > 0 ? (1 - Math.exp(-p.drag * t)) / p.drag : t;
}

/** The launch-speed factor (0…1) that keeps a drift of (vx, vy)·reach from (x, y) inside `b`. */
function fitLaunch(
  vx: number,
  vy: number,
  x: number,
  y: number,
  reach: number,
  b: { left: number; right: number; top: number },
): number {
  let k = 1;
  if (vx > 0) k = Math.min(k, (b.right - x) / (vx * reach));
  else if (vx < 0) k = Math.min(k, (x - b.left) / (-vx * reach));
  if (vy < 0) k = Math.min(k, (y - b.top) / (-vy * reach));
  return Math.max(0, k);
}

/** First time t > 0 at which y0 + vy·t + g·t²/2 reaches floorY (g > 0), or null. */
function landTime(y0: number, vy: number, g: number, floorY: number): number | null {
  const d = vy * vy + 2 * g * (floorY - y0);
  if (d < 0) return null;
  const t = (-vy + Math.sqrt(d)) / g;
  return t > 0 ? t : null;
}
